import React, { useEffect } from 'react';
import SampleCart from './SampleCart';
import { trackEvent } from '../utils/tracking';
import { useSlabData } from '../hooks/useSlabData';
import { dektonSamples, caesarstoneSamples, marbleGraniteSamples, porcelainSamples } from './Sidebar';

// ── EF sample lookup — normalized label/title → sample object ─────────────────
const _norm = (str) =>
  String(str).toLowerCase().replace(/[\s\-_]/g, '').replace(/[^a-z0-9]/g, '');

const ALL_EF_SAMPLES = [
  ...dektonSamples,
  ...caesarstoneSamples,
  ...marbleGraniteSamples,
  ...porcelainSamples,
];

const EF_SAMPLE_MAP = {};
ALL_EF_SAMPLES.forEach(s => {
  // Index by normalized label
  const k1 = _norm(s.label);
  if (k1 && !EF_SAMPLE_MAP[k1]) EF_SAMPLE_MAP[k1] = s;
  // Index by normalized last segment of title (e.g. "Dekton - Somnia" → "somnia")
  if (s.title) {
    const k2 = _norm(s.title.split(' - ').pop());
    if (k2 && !EF_SAMPLE_MAP[k2]) EF_SAMPLE_MAP[k2] = s;
  }
});

/** Find an EF-carried sample matching a similarColor entry from the JSON */
const findEFSample = (similarColor) => {
  const name = similarColor.name || '';
  const nName = _norm(name);
  if (!nName) return null;
  // Exact map hit
  if (EF_SAMPLE_MAP[nName]) return EF_SAMPLE_MAP[nName];
  // Fuzzy: name contains or is contained by a known label
  return ALL_EF_SAMPLES.find(s => {
    const sNorm = _norm(s.label);
    return sNorm.length >= 4 && (nName.includes(sNorm) || sNorm.includes(nName));
  }) || null;
};

// Fallback brand-level data when no JSON entry is matched
const BRAND_TECHNICAL_DATA = {
  'Dekton': {
    name: 'Dekton',
    tagline: 'Ultracompact Surfaces',
    description: 'High-performance sintered stone with exceptional resistance to scratches, stains, heat, and UV rays.',
    applications: ['Kitchen Countertops', 'Bathroom Vanities', 'Facades', 'Flooring'],
    finishes: ['Polished', 'Velvet', 'Matte', 'XGloss', 'Grip+'],
    thicknesses: ['4mm', '8mm', '12mm', '20mm'],
    stainResistant: 'Very High',
    heatResistant: 'Very High',
  },
  'Caesarstone': {
    name: 'Caesarstone Quartz',
    tagline: 'Premium Quartz Surfaces',
    description: 'Premium quartz surfaces offering durability and beauty with a wide range of colors and patterns.',
    applications: ['Kitchen Countertops', 'Bathroom Vanities', 'Wall Cladding'],
    finishes: ['Polished', 'Honed', 'Natural', 'Rough'],
    thicknesses: ['13mm', '20mm', '30mm'],
    stainResistant: 'High',
    heatResistant: 'High',
  },
  'Granite/Marble': {
    name: 'Sensa Natural Stone',
    tagline: 'Natural Stone Surfaces',
    description: 'Premium natural granite and marble with Sentec® stain protection technology for worry-free beauty.',
    applications: ['Kitchen Countertops', 'Bathroom Vanities', 'Flooring'],
    finishes: ['Polished', 'Leathered', 'Honed'],
    thicknesses: ['20mm', '30mm'],
    stainResistant: 'Medium-High',
    heatResistant: 'High',
  },
  'Porcelain': {
    name: 'Atlas Porcelain',
    tagline: 'Large Format Porcelain',
    description: 'Large format porcelain slabs offering versatility, durability, and contemporary aesthetics.',
    applications: ['Kitchen Countertops', 'Bathroom Vanities', 'Wall Cladding', 'Flooring'],
    finishes: ['Polished', 'Silk', 'Matte', 'Hammered'],
    thicknesses: ['6mm', '12mm', '20mm'],
    stainResistant: 'High',
    heatResistant: 'High',
  },
};

// ── Material type education — what users actually identify with ───────────────
const MATERIAL_TYPE_DATA = {
  'Dekton': {
    typeLabel: 'Sintered Stone',
    brandLabel: 'Dekton · Cosentino',
    summary: 'Made by fusing minerals under extreme heat & pressure — the most durable surface available. Zero porosity. No sealing. UV stable for outdoor use.',
    ratings: { scratch: 5, heat: 5, uv: 5, stain: 5, maintenance: 'None' },
  },
  'Quartz': {
    typeLabel: 'Engineered Quartz',
    brandLabel: 'Caesarstone',
    summary: '93% natural quartz crystals bound with polymer resins. Non-porous, consistent colour across the slab, no sealing required. Avoid prolonged direct heat.',
    ratings: { scratch: 4, heat: 3, uv: 3, stain: 5, maintenance: 'None' },
  },
  'Sensa': {
    typeLabel: 'Natural Stone',
    brandLabel: 'Sensa · Granite & Marble',
    summary: 'Quarried from the earth — every slab is unique. Sensa uses Sentec® nano-protection for improved stain resistance without altering the natural look.',
    ratings: { scratch: 4, heat: 5, uv: 4, stain: 3, maintenance: 'Low' },
  },
  'Porcelain': {
    typeLabel: 'Fired Porcelain',
    brandLabel: 'Atlas Plan · Large Format',
    summary: 'Kiln-fired at 1 200 °C from refined clay minerals. Hard, hygienic surface available in large slabs (up to 3×1.5 m) with stone, concrete or wood looks.',
    ratings: { scratch: 4, heat: 4, uv: 5, stain: 5, maintenance: 'None' },
  },
  'Solid': {
    typeLabel: 'Large Format Porcelain',
    brandLabel: 'Solid Collection · EF',
    summary: 'Oversized porcelain slabs for seamless surfaces with minimal visible joins. Ideal for dramatic feature walls, full-height splashbacks and countertops.',
    ratings: { scratch: 4, heat: 4, uv: 5, stain: 5, maintenance: 'None' },
  },
};

const RATING_ICONS = { scratch: '◆', heat: '▲', uv: '◎', stain: '●' };
const RATING_LABELS = { scratch: 'Scratch', heat: 'Heat', uv: 'UV', stain: 'Stain' };
const MAINT_CHIP_COLOR = { None: '#4caf50', Low: '#8bc34a', Moderate: '#ff9800' };

const PerfDots = ({ score }) => (
  <span className="perf-dots">
    {[1,2,3,4,5].map(i => (
      <span key={i} className={`perf-dot${i <= score ? ' on' : ''}`} />
    ))}
  </span>
);

const PRICE_LABELS = {
  entry: 'Entry',
  mid: 'Mid-range',
  premium: 'Premium',
  luxury: 'Luxury',
};

const MAINT_LABELS = {
  none: 'None',
  low: 'Low',
  moderate: 'Moderate',
};

const MAINT_COLORS = {
  none: '#4caf50',
  low: '#8bc34a',
  moderate: '#ff9800',
};

const DurabilityDots = ({ score, max = 5 }) => (
  <span className="durability-dots">
    {Array.from({ length: max }, (_, i) => (
      <span key={i} className={`durability-dot${i < score ? ' filled' : ''}`} />
    ))}
  </span>
);

const MaterialDetails = ({
  activeOverlay,
  activeSubNav,
  selectedSamples,
  onRemoveSample,
  onRequestAllSamples,
  onClearCart,
  isSampleSelectionMode,
  onToggleSampleSelectionMode,
  kitchenView,
  activeView,
  cabinetColor,
  setCabinetColor,
  upperCab = 'green',
  setUpperCab,
  lowerCab = 'black',
  setLowerCab,
  isDownloadView = false,
  onSampleClick,
  onToggleCompare,
  activeSample,
}) => {
  const [showSampleHeader, setShowSampleHeader] = React.useState(true);
  const similarRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const { findSlab } = useSlabData();

  // Resolve rich slab data from JSON catalogue
  const slabData = findSlab(activeOverlay);

  // Material type education block — keyed by sub-nav tab (Dekton/Quartz/Sensa/Porcelain/Solid)
  const materialTypeData = MATERIAL_TYPE_DATA[activeSubNav] || null;

  // Brand-level fallback
  const brandData =
    BRAND_TECHNICAL_DATA[activeSubNav] ||
    BRAND_TECHNICAL_DATA[activeSubNav === 'Quartz' ? 'Caesarstone' : activeSubNav] ||
    BRAND_TECHNICAL_DATA['Dekton'];

  // Display values — JSON first, then fallbacks
  const displayName = slabData?.name
    || (activeOverlay
      ? activeOverlay.split('/').pop().replace(/\.[^/.]+$/, '').split(' - ').pop()
      : 'Select a Material');

  const displayDesc = slabData?.description
    || (activeOverlay
      ? 'High-quality stone surface with excellent durability and aesthetic appeal.'
      : 'Choose a material sample to view details.');

  const finishes = slabData?.finishes || brandData.finishes;
  const thicknesses = slabData?.thicknesses || brandData.thicknesses;
  const applications = slabData?.applications || brandData.applications;
  const stoneType = slabData?.stoneType || slabData?.material || null;
  const collection = slabData?.collection || null;

  // Pre-compute EF-carried similar colors count for the CTA
  const carriedSimilarCount = slabData?.similarColors
    ? slabData.similarColors.map(s => findEFSample(s)).filter(Boolean).length
    : 0;

  const scrollToSimilar = () => {
    if (similarRef.current && panelRef.current) {
      panelRef.current.scrollTo({ top: similarRef.current.offsetTop, behavior: 'smooth' });
    }
  };

  // Track material detail views
  useEffect(() => {
    if (activeOverlay && displayName !== 'Select a Material') {
      trackEvent('material_detail_view', {
        material_name: displayName,
        brand: activeSubNav,
        json_matched: !!slabData,
      });
    }
  }, [activeOverlay]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRequestSampleClick = () => {
    trackEvent('request_sample_click', { material_name: displayName, brand: activeSubNav });
    setShowSampleHeader(false);
    if (onToggleSampleSelectionMode) onToggleSampleSelectionMode();
  };

  return (
    <div className="material-details-panel" ref={panelRef}>
      {/* Slab thumbnail */}
      <div className="material-thumbnail">
        {activeOverlay && (
          <img src={activeOverlay} alt={displayName} className="material-image" />
        )}
      </div>

      {/* Order via WhatsApp */}
      {displayName && displayName !== 'Select a Material' && (() => {
        const productId = activeSample?.id
          || (activeOverlay ? activeOverlay.split('/').pop().replace(/\.[^/.]+$/, '') : '');
        const shareUrl = productId
          ? `${window.location.origin}/?product=${encodeURIComponent(productId)}`
          : window.location.origin;
        const waText = `Hello Elegant Fittings! I'm interested in purchasing:\n\nProduct: ${displayName}\nPage: ${shareUrl}\n\nPlease advise on availability and pricing.`;

        const handleOrderClick = () => {
          // Fire-and-forget to n8n — does not block WhatsApp opening
          fetch('https://elegantfittings.app.n8n.cloud/webhook/ef-whatsapp-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product: displayName,
              productId,
              shareUrl,
              brand: activeSubNav || '',
              timestamp: new Date().toISOString(),
              source: 'visualizer-whatsapp-order',
            }),
          }).catch(() => {});
        };

        return (
          <a
            className="md-whatsapp-order-btn"
            href={`https://wa.me/254745969500?text=${encodeURIComponent(waText)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOrderClick}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.122 1.532 5.856L.057 23.887a.5.5 0 0 0 .606.606l6.031-1.475A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.667-.523-5.184-1.432l-.371-.22-3.835.938.938-3.835-.22-.371A9.965 9.965 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            Order via WhatsApp
          </a>
        );
      })()}

      <div className="material-content">
        <h3 className="panel-title" style={{ fontSize: '12px', marginBottom: '8px' }}>
          MATERIAL DETAILS
        </h3>

        {/* Name + material-type tag */}
        <div className="material-name-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '18px', margin: 0, lineHeight: 1.3 }}>{displayName}</h2>
          {materialTypeData && (
            <span className="material-type-chip">
              {materialTypeData.typeLabel}
            </span>
          )}
        </div>

        {/* Brand + collection subtitle */}
        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.4 }}>
          {[materialTypeData?.brandLabel, collection, stoneType].filter(Boolean).join(' · ')}
        </div>

        {/* See N similar colors CTA */}
        {carriedSimilarCount > 0 && (
          <button className="similar-colors-cta" onClick={scrollToSimilar}>
            See {carriedSimilarCount} similar color{carriedSimilarCount !== 1 ? 's' : ''} →
          </button>
        )}

        {/* Description */}
        <div className="material-description" style={{ marginTop: '10px' }}>
          <p style={{ fontSize: '13px', lineHeight: 1.5, margin: 0, color: '#555' }}>
            {displayDesc}
          </p>
        </div>

        {/* ── Material type education + performance ratings ── */}
        {materialTypeData && (
          <div className="material-type-panel">
            <p className="material-type-summary">{materialTypeData.summary}</p>
            <div className="perf-grid">
              {Object.entries(RATING_ICONS).map(([key, icon]) => (
                <div key={key} className="perf-row">
                  <span className="perf-icon">{icon}</span>
                  <span className="perf-label">{RATING_LABELS[key]}</span>
                  <PerfDots score={materialTypeData.ratings[key]} />
                </div>
              ))}
              <div className="perf-row">
                <span className="perf-icon">✦</span>
                <span className="perf-label">Maintenance</span>
                <span
                  className="maint-chip"
                  style={{ background: MAINT_CHIP_COLOR[materialTypeData.ratings.maintenance] || '#888' }}
                >
                  {materialTypeData.ratings.maintenance}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Specs from JSON ── */}
        <div className="material-specifications" style={{ marginTop: '14px' }}>

          {/* Finishes */}
          <div className="slab-spec-row">
            <span className="slab-spec-label">Finishes</span>
            <div className="slab-tags">
              {finishes.slice(0, 4).map((f, i) => (
                <span key={i} className="slab-tag">{f}</span>
              ))}
            </div>
          </div>

          {/* Thicknesses */}
          <div className="slab-spec-row">
            <span className="slab-spec-label">Thicknesses</span>
            <span className="slab-spec-value">{thicknesses.join(' · ')}</span>
          </div>

          {/* Applications */}
          <div className="slab-spec-row">
            <span className="slab-spec-label">Applications</span>
            <div className="slab-tags">
              {applications.slice(0, 4).map((a, i) => (
                <span key={i} className="slab-tag slab-tag--app">{a}</span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Properties badges (only when JSON matched) ── */}
        {slabData && (
          <div className="slab-properties">
            {slabData.naturalStone !== undefined && (
              <div className="slab-prop">
                <span className="slab-prop-value">
                  {slabData.naturalStone ? '✓' : '✗'}
                </span>
                <span className="slab-prop-label">Natural</span>
              </div>
            )}
            {slabData.outdoorRated !== undefined && (
              <div className="slab-prop">
                <span className="slab-prop-value">
                  {slabData.outdoorRated ? '✓' : '✗'}
                </span>
                <span className="slab-prop-label">Outdoor</span>
              </div>
            )}
            {slabData.durabilityScore && (
              <div className="slab-prop">
                <DurabilityDots score={slabData.durabilityScore} />
                <span className="slab-prop-label">Durability</span>
              </div>
            )}
            {slabData.maintenanceLevel && (
              <div className="slab-prop">
                <span
                  className="slab-prop-value"
                  style={{ color: MAINT_COLORS[slabData.maintenanceLevel] || '#555', fontSize: '11px' }}
                >
                  {MAINT_LABELS[slabData.maintenanceLevel] || slabData.maintenanceLevel}
                </span>
                <span className="slab-prop-label">Care</span>
              </div>
            )}
            {slabData.priceTier && (
              <div className="slab-prop">
                <span className="slab-prop-value" style={{ fontSize: '11px' }}>
                  {PRICE_LABELS[slabData.priceTier] || slabData.priceTier}
                </span>
                <span className="slab-prop-label">Tier</span>
              </div>
            )}
          </div>
        )}

        {/* ── Similar Colors — EF-carried only, clickable ── */}
        {slabData?.similarColors?.length > 0 && (() => {
          const carried = slabData.similarColors
            .map(s => ({ ...s, efSample: findEFSample(s) }))
            .filter(s => s.efSample)
            .slice(0, 4);

          if (!carried.length) return null;

          return (
            <div className="slab-similar" ref={similarRef}>
              <span className="slab-spec-label">Similar Colors</span>
              <div className="slab-similar-list">
                {carried.map((s, i) => (
                  <div key={i} className="slab-similar-item">
                    <button
                      className="slab-similar-visualize"
                      onClick={() => onSampleClick && onSampleClick(s.efSample)}
                      title={`Visualize ${s.name}`}
                    >
                      <img src={s.efSample.image} alt={s.name} className="slab-similar-thumb" />
                      <span className="slab-similar-name">{s.name}</span>
                    </button>
                    <div className="slab-similar-actions">
                      <span className="slab-similar-brand">{s.brand || s.collection}</span>
                      {onToggleCompare && (
                        <button
                          className="slab-similar-compare-btn"
                          title="Compare with current"
                          onClick={() => {
                            onToggleCompare(s.efSample);
                            if (activeSample) onToggleCompare(activeSample);
                          }}
                        >
                          ⇄
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Cabinet Colors Selection (UNCHANGED) ── */}
        {activeView === 'Kitchens' && kitchenView === 1 && (
          <div className="cabinet-colors-section">
            <h3>Cabinet colors</h3>

            {isDownloadView && cabinetColor && (
              <div className="download-cabinet-summary">
                {cabinetColor === '2T' ? (
                  <>
                    <div className="download-cabinet-badge">2 Tone</div>
                    <div className="download-cabinet-2t-cards">
                      {[
                        { role: 'Upper', val: upperCab, colorMap: { '': '#C8B89A', 'W': '#F0EDE8', 'B': '#003764', 'CR': '#E8D5B0', 'G': '#2D5A3D', 'BL': '#1A1A1A' }, nameMap: { '': 'Original', 'W': 'White', 'B': 'Blue', 'CR': 'Cream', 'G': 'Green', 'BL': 'Black' } },
                        { role: 'Lower', val: lowerCab, colorMap: { '': '#C8B89A', 'W': '#F0EDE8', 'B': '#003764', 'BL': '#1A1A1A', 'CR': '#E8D5B0', 'G': '#2D5A3D' }, nameMap: { '': 'Original', 'W': 'White', 'B': 'Blue', 'BL': 'Black', 'CR': 'Cream', 'G': 'Green' } },
                      ].map(({ role, val, colorMap, nameMap }) => (
                        <div key={role} className="download-cabinet-2t-card">
                          <span className="download-cabinet-2t-dot" style={{ background: colorMap[val] || '#C8B89A' }} />
                          <div className="download-cabinet-2t-text">
                            <span className="download-cabinet-2t-role">{role}</span>
                            <span className="download-cabinet-2t-name">{nameMap[val] || 'Original'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="download-cabinet-single">
                    <img src={cabinetColor === 'W' ? '/mainpng2-white-cab.png' : cabinetColor ? `/mainpng2-${cabinetColor}-cab.png` : '/mainpng2-cab.png'} alt="Cabinet" className="download-cabinet-swatch" />
                    <span className="download-cabinet-name">{{ '': 'Original', 'W': 'White', 'B': 'Blue', 'BL': 'Black', 'CR': 'Cream', 'G': 'Green' }[cabinetColor] || cabinetColor}</span>
                  </div>
                )}
              </div>
            )}

            {!isDownloadView && (
              <>
                <div className="cabinet-swatches-grid">
                  {[
                    { id: '', swatch: '/mainpng2-cab.png', name: 'Original' },
                    { id: 'W', swatch: '/mainpng2-white-cab.png', name: 'White' },
                    { id: 'B', swatch: '/mainpng2-B-cab.png', name: 'Blue' },
                    { id: 'BL', swatch: '/mainpng2-BL-cab.png', name: 'Black' },
                    { id: 'CR', swatch: '/mainpng2-CR-cab.png', name: 'Cream' },
                    { id: 'G', swatch: '/mainpng2-G-cab.png', name: 'Green' },
                    { id: '2T', swatch: null, name: '2 Tone' },
                  ].map((color) => (
                    <div
                      key={color.id}
                      onClick={() => {
                        setCabinetColor(color.id);
                        trackEvent('cabinet_color_selected', { color: color.name });
                      }}
                      className={`cabinet-swatch ${cabinetColor === color.id ? 'active' : ''}`}
                      title={color.name}
                    >
                      {color.swatch
                        ? <img src={color.swatch} alt={color.name} />
                        : <div className="cabinet-swatch-2t" />
                      }
                    </div>
                  ))}
                </div>
                {cabinetColor === '2T' && (
                  <div className="cabinet-2t-pickers">
                    {[
                      {
                        label: 'Upper',
                        options: [
                          { val: '', color: '#C8B89A', name: 'Original' },
                          { val: 'W', color: '#F0EDE8', name: 'White' },
                          { val: 'B', color: '#003764', name: 'Blue' },
                          { val: 'CR', color: '#E8D5B0', name: 'Cream' },
                          { val: 'G', color: '#2D5A3D', name: 'Green' },
                          { val: 'BL', color: '#1A1A1A', name: 'Black' },
                        ],
                        active: upperCab,
                        setter: setUpperCab,
                      },
                      {
                        label: 'Lower',
                        options: [
                          { val: '', color: '#C8B89A', name: 'Original' },
                          { val: 'W', color: '#F0EDE8', name: 'White' },
                          { val: 'B', color: '#003764', name: 'Blue' },
                          { val: 'BL', color: '#1A1A1A', name: 'Black' },
                          { val: 'CR', color: '#E8D5B0', name: 'Cream' },
                          { val: 'G', color: '#2D5A3D', name: 'Green' },
                        ],
                        active: lowerCab,
                        setter: setLowerCab,
                      },
                    ].map(({ label, options, active, setter }) => (
                      <div className="cabinet-2t-row" key={label}>
                        <span className="cabinet-2t-label">{label}</span>
                        {options.map(({ val, color, name }) => (
                          <div
                            key={val}
                            className={`cab-color-dot ${active === val ? 'active' : ''}`}
                            style={{ '--dot-color': color }}
                            title={name}
                            onClick={() => setter && setter(val)}
                          >
                            <span className="cab-color-dot-circle" />
                            {active === val && <span className="cab-color-dot-name">{name}</span>}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {isSampleSelectionMode && (
          <div className="selection-mode-info">
            Hit the check out button to proceed with your request
          </div>
        )}

        {/* ── Sample Cart (UNCHANGED) ── */}
        {!isDownloadView && (
          <div className="sample-cart-section">
            <SampleCart
              selectedSamples={selectedSamples}
              onRemoveSample={onRemoveSample}
              onRequestAllSamples={onRequestAllSamples}
              onClearCart={onClearCart}
            />
          </div>
        )}
      </div>

      {/* ── Sticky Request Sample CTA — always visible at bottom of panel ── */}
      {!isDownloadView && activeOverlay && (
        <div className="md-sticky-cta">
          <button
            className="md-sticky-sample-btn"
            onClick={handleRequestSampleClick}
          >
            {isSampleSelectionMode ? 'Done selecting ✓' : 'Request Sample'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MaterialDetails;
